import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  Calendar, TrendingUp, TrendingDown, Minus, Brain, 
  AlertTriangle, BarChart3, Sparkles
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import axios from "axios";

import { API_BASE } from "@/utils/api";

const WeeklyForecastModal = ({ isOpen, onClose, prediction }) => {
  const { t } = useTranslation();
  const [weeklyForecast, setWeeklyForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch weekly forecast when modal opens
  useEffect(() => {
    if (isOpen && !weeklyForecast) {
      const fetchWeeklyForecast = async () => {
        try {
          setLoading(true);
          setError(null);
          const apiUrl = `${API_BASE}/api/mood/forecast/weekly`;
          console.log('Fetching weekly forecast from:', apiUrl);
          const res = await axios.get(apiUrl, { timeout: 15000 });
          if (res.data && res.data.weekly_forecast) {
            setWeeklyForecast(res.data);
          } else {
            throw new Error('Invalid response format');
          }
        } catch (err) {
          console.error("Failed to fetch weekly forecast:", err);
          setError(err.message || 'Failed to load forecast');
        } finally {
          setLoading(false);
        }
      };
      fetchWeeklyForecast();
    }
  }, [isOpen, weeklyForecast]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
    }
  }, [isOpen]);

  const TrendIcon = weeklyForecast?.trend === 'improving' ? TrendingUp : 
                    weeklyForecast?.trend === 'worsening' ? TrendingDown : Minus;

  const trendColor = weeklyForecast?.trend === 'improving' ? '#22c55e' : 
                     weeklyForecast?.trend === 'worsening' ? '#ef4444' : '#6b7280';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="apple-card border-0 max-w-2xl w-[90vw] max-h-[80vh] overflow-y-auto modal-mobile-optimized modal-glow"
        style={{
          '--modal-glow-color': trendColor || 'rgba(255,255,255,0.2)',
          '--modal-glow-color-faded': trendColor ? `${trendColor}50` : 'rgba(255,255,255,0.15)',
          '--modal-glow-color-dim': trendColor ? `${trendColor}30` : 'rgba(255,255,255,0.08)',
        }}
      >
        <DialogHeader>
          <DialogTitle className="apple-headline text-lg flex items-center gap-2">
            <Calendar size={20} style={{ color: 'var(--apple-blue)' }} />
            {t('sevenDayForecast') || '7-Day Mood Forecast'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="weekly-forecast-modal mt-4 space-y-5">
          {loading ? (
            <div className="forecast-modal-loading">
              <div className="loading-indicator">
                <div className="loading-spinner"></div>
                <p className="loading-title">Generating 7-Day Forecast</p>
                <p className="loading-subtitle">Analyzing global news patterns, social sentiment, and historical mood data...</p>
              </div>
              <div className="loading-steps">
                <div className="loading-step">
                  <BarChart3 size={14} />
                  <span>Processing 100+ news sources</span>
                </div>
                <div className="loading-step">
                  <Brain size={14} />
                  <span>Running AI prediction models</span>
                </div>
                <div className="loading-step">
                  <TrendingUp size={14} />
                  <span>Calculating trend trajectories</span>
                </div>
              </div>
              <div className="shimmer" style={{ width: '100%', height: '80px', borderRadius: '12px', marginTop: '16px' }} />
              <div className="shimmer" style={{ width: '100%', height: '120px', borderRadius: '12px', marginTop: '12px' }} />
            </div>
          ) : weeklyForecast?.weekly_forecast ? (
            <>
              {/* Synopsis Section */}
              {weeklyForecast.synopsis && (
                <div className="forecast-modal-section">
                  <div className="forecast-modal-section-header">
                    <Brain size={14} />
                    <span>{t('forecastMethodology') || 'Forecast Methodology'}</span>
                  </div>
                  <p className="forecast-modal-synopsis">
                    {weeklyForecast.synopsis}
                  </p>
                  <div className="forecast-modal-meta">
                    <span>
                      <BarChart3 size={12} />
                      {weeklyForecast.data_points_analyzed || 100} data points analyzed
                    </span>
                  </div>
                </div>
              )}

              {/* Trend Indicator */}
              <div className="forecast-modal-trend-section">
                <span className="forecast-modal-trend-label">Overall Trend</span>
                <div 
                  className="forecast-modal-trend-badge"
                  style={{ backgroundColor: `${trendColor}15`, color: trendColor }}
                >
                  <TrendIcon size={14} />
                  <span>{weeklyForecast.trend?.toUpperCase()}</span>
                </div>
              </div>

              {/* 7-Day Cards Grid */}
              <div className="forecast-modal-days">
                {weeklyForecast.weekly_forecast.map((day, idx) => (
                  <motion.div 
                    key={idx}
                    className="forecast-modal-day"
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <div className="forecast-day-header">
                      <span className="forecast-day-label">{day.day}</span>
                      <span className="forecast-day-confidence">{Math.round(day.confidence * 100)}%</span>
                    </div>
                    <motion.div 
                      className="forecast-day-orb"
                      style={{ backgroundColor: day.color }}
                      animate={{ 
                        boxShadow: [`0 0 10px ${day.color}40`, `0 0 20px ${day.color}60`, `0 0 10px ${day.color}40`]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <div className="forecast-day-adjective" style={{ color: day.color }}>
                      {day.adjective}
                    </div>
                    {day.pantone && (
                      <div className="forecast-day-pantone">
                        {day.pantone.name}
                      </div>
                    )}
                    <div className="forecast-day-range-wrapper">
                      <div className="forecast-day-range-bar">
                        <motion.div 
                          className="forecast-day-range-fill"
                          style={{ backgroundColor: day.color }}
                          initial={{ width: 0 }}
                          animate={{ width: `${day.confidence * 100}%` }}
                          transition={{ delay: idx * 0.05 + 0.2, duration: 0.5 }}
                        />
                      </div>
                      <div className="forecast-day-range-labels">
                        <span>{Math.round((day.low || day.intensity * 0.9) * 100)}%</span>
                        <span>{Math.round((day.high || day.intensity * 1.1) * 100)}%</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Key Drivers & Risk Factors */}
              <div className="forecast-modal-details">
                {weeklyForecast.key_drivers && weeklyForecast.key_drivers.length > 0 && (
                  <div className="forecast-modal-detail-section">
                    <div className="forecast-modal-detail-header">
                      <Sparkles size={14} style={{ color: '#22c55e' }} />
                      <span>{t('keyDrivers') || 'Key Drivers'}</span>
                    </div>
                    <div className="forecast-modal-tags">
                      {weeklyForecast.key_drivers.map((driver, i) => (
                        <motion.span 
                          key={i} 
                          className="forecast-modal-driver-tag"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.05 }}
                        >
                          {driver}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                )}
                
                {weeklyForecast.risk_factors && weeklyForecast.risk_factors.length > 0 && (
                  <div className="forecast-modal-detail-section">
                    <div className="forecast-modal-detail-header">
                      <AlertTriangle size={14} style={{ color: '#ef4444' }} />
                      <span>{t('riskFactors') || 'Risk Factors'}</span>
                    </div>
                    <div className="forecast-modal-tags">
                      {weeklyForecast.risk_factors.map((risk, i) => (
                        <motion.span 
                          key={i} 
                          className="forecast-modal-risk-tag"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.05 }}
                        >
                          {risk}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Disclaimer */}
              <div className="forecast-modal-disclaimer">
                <Sparkles size={12} />
                <span>Forecast based on historical mood patterns and current event analysis. Actual mood may vary based on emerging global events.</span>
              </div>
            </>
          ) : (
            <div className="forecast-modal-error">
              <AlertTriangle size={24} />
              <p>Unable to load 7-day forecast.</p>
              {error && <p className="forecast-error-detail">{error}</p>}
              <button 
                className="forecast-retry-btn"
                onClick={() => {
                  setWeeklyForecast(null);
                  setError(null);
                }}
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { WeeklyForecastModal };
